import classNames from 'classnames';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useEffect, useMemo, useState } from 'react';

type ListBoxInputProps<T> = {
  className?: string;
  size?: 'md' | 'sm';
  defaultValue: T;
  options: Array<{
    value: T;
    label: string;
  }>;
  onChange: (value: T) => void;
};

export const ListBoxInput = <T extends string | number | symbol>({
  className,
  size = 'md',
  defaultValue,
  options,
  onChange,
}: ListBoxInputProps<T>) => {
  const [selected, setSelected] = useState(defaultValue);

  const { normalizedOptions, valueToKey, keyToOption } = useMemo(() => {
    const normalizedOptions = options.map((option, index) => ({
      key: `option-${index}`,
      option,
    }));
    const valueToKey = new Map<T, string>();
    const keyToOption = new Map<string, (typeof options)[number]>();

    normalizedOptions.forEach(({ key, option }) => {
      valueToKey.set(option.value, key);
      keyToOption.set(key, option);
    });

    return { normalizedOptions, valueToKey, keyToOption };
  }, [options]);

  const selectedKey = valueToKey.get(selected);
  const selectedOption = selectedKey
    ? keyToOption.get(selectedKey)
    : options.find((item) => item.value === selected);

  const handleSelect = (key: string) => {
    const option = keyToOption.get(key);
    if (!option) {
      return;
    }
    setSelected(option.value);
    onChange(option.value);
  };

  useEffect(() => {
    setSelected(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return (
    <div className={classNames(className, 'relative')}>
      <Select value={selectedKey} onValueChange={handleSelect}>
        <SelectTrigger
          className={classNames(size === 'sm' && 'h-8 px-2 py-1 text-xs gap-2')}
        >
          <SelectValue placeholder={selectedOption?.label ?? ''} />
        </SelectTrigger>
        <SelectContent
          className={classNames(
            'max-h-60 overflow-y-auto',
            size === 'sm' && 'text-xs'
          )}
        >
          {normalizedOptions.map(({ key, option }) => (
            <SelectItem
              key={key}
              value={key}
              data-testid="list-boxinput"
              className={classNames(size === 'sm' && 'py-1 text-xs')}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
